// Guest mobile-number normalisation. Confirmations and reminders go out via
// SMS / WhatsApp, and both fail silently on numbers that aren't in
// international (E.164) format — so the checkout form must only ever submit
// +<country><number>.
//
// No libphonenumber here (the widget stays lean): we normalise the common
// input shapes and validate the E.164 envelope (+, 8–15 digits). Local
// 0-prefixed numbers get the tenant-market default country code (South
// Africa, +27) — a guest from elsewhere types their own +CC anyway.

const DEFAULT_CC = "27";

export interface PhoneResult {
  ok: boolean;
  /** E.164, e.g. "+27821234567". Only set when ok. */
  e164?: string;
  error?: string;
}

const INVALID_MSG =
  "Please enter your mobile number in international format, e.g. +27 82 123 4567 — booking updates are sent by SMS/WhatsApp and need the country code.";

const SWAPPED_ZERO_MSG =
  "That looks like a South African number with a + in place of the leading 0. Enter it as 082 123 4567, or in full as +27 82 123 4567.";

// The national significant length of a local number, the digits a local
// MOBILE can start with (ZA mobiles are 06x / 07x / 08x, so the NSN begins
// 6, 7 or 8), and a sample to quote back. Nine digits after the plus is
// vanishingly rare internationally — the countries that use it are small
// territories whose codes start 2 or 3 — so the leading-digit test catches
// the swapped-zero typo without rejecting a real foreign guest.
const LOCAL_MOBILE_SHAPE: Record<
  string,
  { nsn: number; leading: RegExp; country: string; example: string }
> = {
  "27": {
    nsn: 9,
    leading: /^[6-8]/,
    country: "South African",
    example: "082 123 4567",
  },
};

// Quotes the count WITHOUT claiming where the guest's digits sat: the same
// check fires on "083310416" and on "+2783310416", and telling someone who
// typed a country code that they have too few digits "after the leading 0"
// describes a number they did not enter.
const wrongLengthMsg = (
  got: number,
  shape: (typeof LOCAL_MOBILE_SHAPE)[string],
) =>
  `That looks like ${got} digits — a ${shape.country} number has ${shape.nsn} ` +
  `after the country code (e.g. ${shape.example}, or +27 82 123 4567). ` +
  `Check for a missing or extra digit.`;

export function normalizePhoneE164(
  raw: string,
  defaultCc: string = DEFAULT_CC,
): PhoneResult {
  // Keep digits and a leading + only ("082 123-4567", "(082) 123 4567", …).
  const compact = raw.trim().replace(/[\s().\- ]/g, "");
  if (!compact) return { ok: false, error: INVALID_MSG };

  let candidate = compact;
  if (candidate.startsWith("00")) candidate = `+${candidate.slice(2)}`;

  if (!candidate.startsWith("+")) {
    if (!/^\d+$/.test(candidate)) return { ok: false, error: INVALID_MSG };
    if (candidate.startsWith("0")) {
      // Local format (e.g. 0821234567) → default market country code.
      candidate = `+${defaultCc}${candidate.slice(1)}`;
    } else if (candidate.startsWith(defaultCc) && candidate.length >= 10) {
      // Typed the country code but dropped the plus (27821234567).
      candidate = `+${candidate}`;
    } else {
      return { ok: false, error: INVALID_MSG };
    }
  }

  // E.164: leading +, no leading zero, 8–15 digits total. (Strictly E.164
  // allows 7-digit numbers, but no mobile network we message uses them —
  // requiring 8 catches truncated input.)
  if (!/^\+[1-9]\d{7,14}$/.test(candidate)) {
    return { ok: false, error: INVALID_MSG };
  }

  // ── The "+ where the 0 goes" typo ───────────────────────────────────────
  // A guest who knows the number needs a plus, and swaps it for the leading
  // zero, types "+825727456" for 082 572 7456. That is a SYNTACTICALLY VALID
  // E.164 string (+82 is South Korea), so the envelope test above waves it
  // through — and the number is dead for both SMS and WhatsApp. Two Laborie
  // spa guests were stored exactly this way on 13 Aug 2026; nobody noticed
  // until their bookings were opened side by side.
  //
  // We reject rather than "correct" it. Prepending the country code ourselves
  // would be guessing at a contact number, and a wrong guess sends a guest's
  // booking details to a stranger. Telling them what to type is safe; deciding
  // on their behalf is not.
  const shape = LOCAL_MOBILE_SHAPE[defaultCc];
  const nsn = candidate.slice(1);
  if (
    shape &&
    !nsn.startsWith(defaultCc) &&
    nsn.length === shape.nsn &&
    shape.leading.test(nsn)
  ) {
    return { ok: false, error: SWAPPED_ZERO_MSG };
  }

  // ── Wrong-length local numbers ──────────────────────────────────────────
  // "083310416" is a real 083 number with a digit dropped. The envelope test
  // above waves it through — +2783310416 is syntactically valid E.164 — and
  // we then hand it to WhatsApp, which answers 422 "the attribute is not
  // valid" and the guest never hears back. A Laborie function enquiry died
  // exactly this way on 14 Aug 2026.
  //
  // Length alone is decisive here: EVERY South African number, mobile or
  // landline, has a 9-digit national significant number. Only applied to the
  // default market — we don't hold a length table for the rest of the world,
  // and guessing one would reject real foreign guests.
  if (shape && nsn.startsWith(defaultCc)) {
    const national = nsn.slice(defaultCc.length);
    if (national.length !== shape.nsn) {
      return { ok: false, error: wrongLengthMsg(national.length, shape) };
    }
  }

  return { ok: true, e164: candidate };
}

/**
 * Optional-field variant: empty input is fine (returns null); anything
 * non-empty must normalise, else the caller gets ok:false.
 */
export function normalizeOptionalPhone(
  raw: string,
  defaultCc: string = DEFAULT_CC,
): { ok: boolean; e164: string | null; error?: string } {
  if (!raw.trim()) return { ok: true, e164: null };
  const result = normalizePhoneE164(raw, defaultCc);
  return result.ok
    ? { ok: true, e164: result.e164! }
    : { ok: false, e164: null, error: result.error };
}
